import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Security, 
  AccountBalance, 
  Description, 
  Visibility,
  Lock,
  AccessTime,
  Shield,
  InsertDriveFile
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';

const KeyVault: React.FC = () => {
  const { user } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Hardcoded vault items
  const vaultItems = [
    {
      id: '1',
      name: 'Bank Account Details',
      type: 'financial',
      encrypted: true,
      lastAccessed: '2024-09-20T10:30:00Z'
    },
    {
      id: '2',
      name: 'Property Deeds',
      type: 'legal',
      encrypted: true,
      lastAccessed: '2024-09-19T14:15:00Z'
    },
    {
      id: '3',
      name: 'Investment Portfolio',
      type: 'financial',
      encrypted: true,
      lastAccessed: '2024-09-18T09:45:00Z'
    },
    {
      id: '4',
      name: 'Insurance Policies',
      type: 'legal',
      encrypted: true,
      lastAccessed: '2024-09-17T16:20:00Z'
    },
    {
      id: '5',
      name: 'Business Contracts',
      type: 'legal',
      encrypted: true,
      lastAccessed: '2024-09-16T11:10:00Z'
    },
    {
      id: '6',
      name: 'Personal ID Documents',
      type: 'personal',
      encrypted: true,
      lastAccessed: '2024-09-15T13:30:00Z'
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'financial': return <AccountBalance sx={{ fontSize: 40 }} />;
      case 'legal': return <Description sx={{ fontSize: 40 }} />;
      case 'personal': return <Security sx={{ fontSize: 40 }} />;
      default: return <Lock sx={{ fontSize: 40 }} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'financial': return { color: '#34A853', bgColor: '#E8F5E8' };
      case 'legal': return { color: '#4285F4', bgColor: '#E3F2FD' };
      case 'personal': return { color: '#EA4335', bgColor: '#FFEBEE' };
      default: return { color: '#607D8B', bgColor: '#ECEFF1' };
    }
  };

  const statCards = [
    {
      title: 'Total Documents',
      value: vaultItems.length,
      icon: <InsertDriveFile sx={{ fontSize: 40 }} />,
      color: '#4285F4',
      bgColor: '#E3F2FD'
    },
    {
      title: 'Financial Docs',
      value: vaultItems.filter(item => item.type === 'financial').length,
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      color: '#34A853',
      bgColor: '#E8F5E8'
    },
    {
      title: 'Legal Docs',
      value: vaultItems.filter(item => item.type === 'legal').length,
      icon: <Description sx={{ fontSize: 40 }} />,
      color: '#FBBC04',
      bgColor: '#FFF8E1'
    },
    {
      title: 'Encrypted',
      value: vaultItems.filter(item => item.encrypted).length,
      icon: <Shield sx={{ fontSize: 40 }} />,
      color: '#EA4335',
      bgColor: '#FFEBEE'
    }
  ];

  if (user?.role !== 'PLUS') {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80,
              bgcolor: '#9C27B0',
              mx: 'auto',
              mb: 3
            }}
          >
            <Security sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Key Vault
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This feature is available for PLUS plan only.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upgrade to PLUS to access secure storage for your financial and legal documents.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Avatar 
          sx={{ 
            width: { xs: 60, md: 80 }, 
            height: { xs: 60, md: 80 },
            bgcolor: '#9C27B0',
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          <Security sx={{ fontSize: { xs: 30, md: 40 } }} />
        </Avatar>
        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            sx={{ 
              fontWeight: 600,
              color: '#333',
              mb: 0.5
            }}
          >
            Key Vault
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Secure storage with AES-256 encryption
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${card.bgColor} 0%, ${card.bgColor}CC 100%)`,
                border: 'none',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                },
                height: '100%'
              }}
            >
              <CardContent sx={{ 
                p: { xs: 2.5, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Box
                  sx={{
                    width: { xs: 60, md: 70 },
                    height: { xs: 60, md: 70 },
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: card.color,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}
                >
                  {card.icon}
                </Box>
                
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  sx={{
                    fontWeight: 700,
                    color: '#333',
                    mb: 1,
                    fontSize: { xs: '1.8rem', md: '2.5rem' }
                  }}
                >
                  {card.value}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    fontSize: { xs: '0.85rem', md: '0.9rem' }
                  }}
                >
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Vault Items */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Stored Documents
            </Typography>
          </Box>
          
          <Grid container spacing={0}>
            {vaultItems.map((item, index) => {
              const colors = getTypeColor(item.type);
              return (
                <Grid item xs={12} md={6} key={item.id}>
                  <Box 
                    sx={{ 
                      p: 3, 
                      borderBottom: { xs: '1px solid #f0f0f0', md: index < vaultItems.length - 2 ? '1px solid #f0f0f0' : 'none' },
                      borderRight: { md: index % 2 === 0 ? '1px solid #f0f0f0' : 'none' },
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box 
                        sx={{ 
                          mr: 2,
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: colors.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: colors.color
                        }}
                      >
                        {getTypeIcon(item.type)}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Chip 
                          label={item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                          sx={{
                            backgroundColor: colors.bgColor,
                            color: colors.color,
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                          size="small"
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Lock sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        {item.encrypted ? 'AES-256 Encrypted' : 'Not Encrypted'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Last accessed: {new Date(item.lastAccessed).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        size="small" 
                        startIcon={<Visibility />}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        View
                      </Button>
                      <Button 
                        size="small"
                        variant="text"
                        sx={{ borderRadius: 2 }}
                      >
                        Download
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Features List */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Key Vault Features
          </Typography>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: '#4285F4', width: 40, height: 40 }}>
                  <Security />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="AES-256 Encryption"
                secondary="Military-grade encryption for all stored documents"
                sx={{ ml: 2 }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: '#34A853', width: 40, height: 40 }}>
                  <AccountBalance />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Financial Document Storage"
                secondary="Bank statements, investment records, insurance policies"
                sx={{ ml: 2 }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: '#FBBC04', width: 40, height: 40 }}>
                  <Description />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary="Legal Document Management"
                secondary="Contracts, deeds, certificates, and other legal documents"
                sx={{ ml: 2 }}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default KeyVault;
